import { Stack, Box, Card, Typography, Link } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { usePoliciesQuery } from '../../../api/getPolicies';
import {
  linkCustomerPolicy,
  LinkCustomerPolicyArgs,
} from '../../../api/linkCustomerPolicy';
import colors from '../../../constants/colors';
import useAnalytics from '../../../hooks/useAnalytics';
import { useSnackbarsContext } from '../../../store/SnackbarProvider';
import { useRecentlyLinkedContext } from '../../../store/recentlyLinkedPolicy';
import { preventNonNumbers } from '../../../utils/helpers';
import ControlledTextField from '../../TextField/ControlledTextField';
import Button from '../../styledComponents/Button';

type LinkPolicyFormValue = {
  lastFourDigits: string;
};

const PolicyLinkCard = () => {
  const { track } = useAnalytics();
  const [findPolicyToggled, setFindPolicyToggled] = useState<boolean>(false);
  const [lastFourDigitsValue, setLastFourDigitsValue] = useState('');
  const { control, handleSubmit } = useForm<LinkPolicyFormValue>();
  const { enqueueSnackbar, closeSnackbar } = useSnackbarsContext();
  const queryClient = useQueryClient();
  const { data: policyData } = usePoliciesQuery();
  const { setRecentlyLinkedPolicyNumber } = useRecentlyLinkedContext();

  const lastFourOfAddedPolicies = policyData?.policies.map((policy) => {
    return policy.policyNumber.slice(-4);
  });

  const handleChange =
    (fieldName: string) =>
    (
      e: React.ChangeEvent<HTMLInputElement>,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    ) =>
      preventNonNumbers(e.target.value, (val: string) => {
        onChange(e);
        fieldName === 'lastFourDigits' &&
          setLastFourDigitsValue(e.target.value);
      });

  const linkPolicy = useMutation({
    mutationFn: (args: LinkCustomerPolicyArgs) => linkCustomerPolicy(args),
  });

  const handleLastFourDigitsSubmit = async () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    try {
      closeSnackbar();
      const linkedPolicyData = await linkPolicy.mutateAsync({
        lastFourDigits: lastFourDigitsValue,
      });
      enqueueSnackbar({
        variant: 'success',
        message: `Your ${linkedPolicyData[0].policyType.toLowerCase()} policy ${
          linkedPolicyData[0].policyNumber
        } is linked.`,
        autoHideDuration: 10000,
        key: 'linkPolicySuccess',
      });
      setRecentlyLinkedPolicyNumber(linkedPolicyData[0].policyNumber);
      await queryClient.invalidateQueries({
        queryKey: ['welcome'],
      });
      await queryClient.invalidateQueries({
        queryKey: ['policies'],
      });
      await queryClient.invalidateQueries({
        queryKey: ['bills'],
      });
    } catch (err) {
      if (lastFourOfAddedPolicies?.includes(lastFourDigitsValue)) {
        enqueueSnackbar({
          variant: 'error',
          message: 'This policy has been already added to your account.',
          key: 'linkPolicyError',
        });
      } else {
        enqueueSnackbar({
          variant: 'error',
          title: 'We are unable to link that policy.',
          key: 'linkPolicyError',
          content: (
            <>
              Please contact Service at{' '}
              <Link
                href="tel:+888.980.5650"
                underline="none"
                sx={{
                  fontSize: 18,
                  fontWeight: 400,
                  fontFamily: 'Cabin',
                  fontKerning: 'normal',
                  color: colors.csaaSpectrumBlue50,
                }}
              >
                888.980.5650
              </Link>
              .
            </>
          ),
        });
      }
    }
  };

  const toggleFindPolicy = () => {
    track('Link Policy Initiated', {
      event_type: 'Link Accessed',
      contents: 'Link your policy',
      link: 'Link your policy',
      location: 'Secondary Card',
    });
    setFindPolicyToggled(true);
  };

  const renderFindPolicy = () => {
    return findPolicyToggled ? (
      <Stack sx={{ width: '100%' }}>
        <Typography
          variant="h2"
          sx={{
            fontSize: 18,
            fontWeight: 700,
            color: colors.csaaBlack,
            lineHeight: '25px',
            width: 1,
            mb: { mobile: 2, desktop: 3 },
          }}
        >
          Don&apos;t see your policy?
        </Typography>
        <ControlledTextField
          control={control}
          label="Last 4 digits of policy number"
          name="lastFourDigits"
          variant="outlined"
          inputProps={{ maxLength: 4 }}
          InputProps={{ required: true }}
          sx={{
            mb: {
              mobile: 3,
              tablet: 5,
              desktop: 1,
            },
          }}
          handleChange={handleChange('lastFourDigits')}
        />
        <Button
          variant="outlined"
          type="submit"
          color="inherit"
          data-testid="policyFindButton"
          disableRipple
          loading={linkPolicy.isPending}
          sx={{
            width: { mobile: 1, tablet: 'auto', desktop: 1 },
            borderWidth: 2,
            fontWeight: 700,
            textTransform: 'none',
            fontSize: 18,
            color: colors.csaaBlack,
            px: { mobile: 4, desktop: 3 },
            wordWrap: 'nowrap',
            minWidth: 'max-content',
          }}
          disabled={lastFourDigitsValue.length < 4 || linkPolicy.isPending}
        >
          Find policy
        </Button>
      </Stack>
    ) : (
      <Stack
        direction={{ mobile: 'column', tablet: 'row', desktop: 'column' }}
        alignItems="center"
        sx={{ width: '100%' }}
      >
        <Typography
          variant="h2"
          sx={{
            fontSize: 18,
            fontWeight: 700,
            color: colors.csaaBlack,
            lineHeight: '25px',
            width: 1,
            mb: { mobile: 2, tablet: 0, desktop: 3 },
          }}
        >
          Don&apos;t see your policy?
        </Typography>
        <Button
          variant="outlined"
          color="inherit"
          data-testid="policyLinkButton"
          sx={{
            width: { mobile: 1, tablet: 'auto', desktop: 1 },
            borderWidth: 2,
            fontWeight: 700,
            textTransform: 'none',
            fontSize: 18,
            color: colors.csaaBlack,
            px: { mobile: 4, desktop: 3 },
            wordWrap: 'nowrap',
            minWidth: 'max-content',
          }}
          onClick={() => toggleFindPolicy()}
        >
          Link your policy
        </Button>
      </Stack>
    );
  };

  useEffect(
    () => () => {
      closeSnackbar('linkPolicySuccess');
      closeSnackbar('linkPolicyError');
    },
    []
  );

  return (
    <Card
      data-testid="policyLinkCard"
      component={Box}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0px 4px 8px rgba(25, 30, 43, 0.08)',
        px: { mobile: 2, tablet: 5, desktop: 2 },
        py: { mobile: 2, tablet: 3, desktop: 2 },
        borderRadius: 2,
        height: 'auto',
      }}
    >
      <form onSubmit={handleSubmit(handleLastFourDigitsSubmit)}>
        <Stack>{renderFindPolicy()}</Stack>
      </form>
    </Card>
  );
};

export default PolicyLinkCard;
